import {Badge, Button, Checkbox, Modal} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useCallback, useMemo, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import type {
  FormPackageManifestItem} from 'src/redux/queries/form';
import {
  useGetFormPackageManifestQuery,
  usePackageFormMutation,
} from 'src/redux/queries/form';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useDocumentDownload} from 'src/shared/hooks/useDocumentDownload';

export interface PackageFormModalProps {
  opened: boolean;
  onClose: () => void;
  formId: number;
  formIdentifier: string | null | undefined;
}

interface PackageFormValues {
  includePdf: boolean;
}

/**
 * Groups manifest items by their sourceGroup field.
 */
function groupBySource(
  items: FormPackageManifestItem[]
): Map<string, FormPackageManifestItem[]> {
  const map = new Map<string, FormPackageManifestItem[]>();
  for (const item of items) {
    const group = map.get(item.sourceGroup) ?? [];
    group.push(item);
    map.set(item.sourceGroup, group);
  }
  return map;
}

export function PackageFormModal({
  opened,
  onClose,
  formId,
  formIdentifier,
}: PackageFormModalProps) {
  const {setAlert} = useAlert();
  const {download} = useDocumentDownload();

  const {data: manifest, isLoading: isManifestLoading} =
    useGetFormPackageManifestQuery(formId, {skip: !opened});

  const [packageForm, {isLoading: isPackaging}] = usePackageFormMutation();

  const {control, handleSubmit} = useForm<PackageFormValues>({
    defaultValues: {includePdf: true},
  });

  // Track which keys are selected (default: all)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // When manifest loads, default-select all *available* keys. Unavailable files
  // (missing from storage) cannot be packaged, so they are never selectable.
  const allKeys = useMemo(
    () =>
      new Set(manifest?.items.filter(i => i.available).map(i => i.key) ?? []),
    [manifest]
  );

  // Use allKeys as selected when selectedKeys is empty (initial state) to
  // represent "all selected". We track deselections explicitly.
  const effectiveSelected = useMemo((): Set<string> => {
    // On first load selectedKeys is empty — treat as all selected.
    if (selectedKeys.size === 0 && allKeys.size > 0) {
      return allKeys;
    }
    return selectedKeys;
  }, [selectedKeys, allKeys]);

  const isKeySelected = useCallback(
    (key: string) => effectiveSelected.has(key),
    [effectiveSelected]
  );

  const toggleKey = useCallback(
    (key: string) => {
      setSelectedKeys(prev => {
        // On first toggle from "all selected" state, build the full set first.
        const base =
          prev.size === 0 && allKeys.size > 0
            ? new Set(allKeys)
            : new Set(prev);
        if (base.has(key)) {
          base.delete(key);
        } else {
          base.add(key);
        }
        return base;
      });
    },
    [allKeys]
  );

  const toggleGroup = useCallback(
    (groupKeys: string[], checked: boolean) => {
      setSelectedKeys(prev => {
        const base =
          prev.size === 0 && allKeys.size > 0
            ? new Set(allKeys)
            : new Set(prev);
        for (const k of groupKeys) {
          if (checked) {
            base.add(k);
          } else {
            base.delete(k);
          }
        }
        return base;
      });
    },
    [allKeys]
  );

  const grouped = useMemo(
    () => groupBySource(manifest?.items ?? []),
    [manifest]
  );

  // includeRelatedActions = any selected key belongs to an "Action: ..." group
  const includeRelatedActions = useMemo(() => {
    for (const [group, items] of grouped.entries()) {
      if (group.startsWith('Action: ')) {
        if (items.some(i => effectiveSelected.has(i.key))) {
          return true;
        }
      }
    }
    return false;
  }, [grouped, effectiveSelected]);

  const onExport = async (values: PackageFormValues) => {
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const identifier = formIdentifier ?? String(formId);
    const downloaded = await download(
      timeZone =>
        packageForm({
          formId,
          includeRelatedActions,
          includePdf: values.includePdf,
          selectedKeys: Array.from(effectiveSelected),
          timeZone,
        }).unwrap(),
      `FormExport_${identifier}_${dateStr}.zip`
    );
    if (downloaded) {
      setAlert('Form package exported successfully', 'success');
      onClose();
    }
  };

  const isExportDisabled =
    isPackaging || (effectiveSelected.size === 0 && allKeys.size > 0);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Package Form"
      size="md"
      styles={{title: {fontWeight: 700}}}
      centered>
      {isManifestLoading ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : (
        <form onSubmit={handleSubmit(onExport)}>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Select the files to include in the export package.
            </Text>

            {Array.from(grouped.entries()).map(([group, items]) => {
              // Only available files participate in selection / select-all.
              const groupKeys = items.filter(i => i.available).map(i => i.key);
              const allGroupSelected =
                groupKeys.length > 0 &&
                groupKeys.every(k => effectiveSelected.has(k));
              const someGroupSelected =
                !allGroupSelected &&
                groupKeys.some(k => effectiveSelected.has(k));

              return (
                <Box key={group}>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={600}>
                      {group}
                    </Text>
                    <Checkbox
                      size="sm"
                      label="Select all"
                      checked={allGroupSelected}
                      indeterminate={someGroupSelected}
                      disabled={groupKeys.length === 0}
                      onChange={e =>
                        toggleGroup(groupKeys, e.currentTarget.checked)
                      }
                    />
                  </Group>
                  <Stack gap="xs" pl="sm">
                    {items.map(item => (
                      <Checkbox
                        key={item.key}
                        size="sm"
                        disabled={!item.available}
                        label={
                          <Text
                            size="sm"
                            c={item.available ? undefined : 'dimmed'}>
                            {item.displayName}
                            {item.originLabel ? (
                              <Text span size="xs" c="dimmed">
                                {' '}
                                — {item.originLabel}
                              </Text>
                            ) : null}
                            {!item.available ? (
                              <Badge
                                color="red"
                                variant="light"
                                size="xs"
                                ml="xs">
                                Unavailable
                              </Badge>
                            ) : null}
                          </Text>
                        }
                        checked={item.available && isKeySelected(item.key)}
                        onChange={() => item.available && toggleKey(item.key)}
                      />
                    ))}
                  </Stack>
                  <Divider mt="sm" />
                </Box>
              );
            })}

            <Controller
              name="includePdf"
              control={control}
              render={({field}) => (
                <Checkbox
                  size="sm"
                  label="Include form PDF"
                  checked={field.value}
                  onChange={e => field.onChange(e.currentTarget.checked)}
                />
              )}
            />

            <Group justify="flex-end" mt="sm">
              <Button
                type="button"
                variant="subtle"
                color="gray"
                onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isPackaging}
                disabled={isExportDisabled}>
                Export
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
